import { useState, useEffect, useCallback } from "react";
import { Button } from "@/components/ui/button";
import { X, ChevronRight, ChevronLeft } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";

export interface GuideStep {
  targetSelector: string;
  title: string;
  description: string;
}

interface OnboardingGuideProps {
  steps: GuideStep[];
  onComplete: () => void;
  onSkip: () => void;
}

interface TargetRect {
  top: number;
  left: number;
  width: number;
  height: number;
}

const PADDING = 8;

const OnboardingGuide = ({ steps, onComplete, onSkip }: OnboardingGuideProps) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [targetRect, setTargetRect] = useState<TargetRect | null>(null);
  const [tooltipPos, setTooltipPos] = useState<{ top: number; left: number; placement: string }>({
    top: 0,
    left: 0,
    placement: "bottom",
  });

  const step = steps[currentStep];

  const measureTarget = useCallback(() => {
    if (!step) return;
    const el = document.querySelector(step.targetSelector);
    if (!el) {
      // If element not found, show tooltip centered
      setTargetRect(null);
      setTooltipPos({
        top: window.innerHeight / 2 - 80,
        left: window.innerWidth / 2 - 160,
        placement: "center",
      });
      return;
    }
    const rect = el.getBoundingClientRect();
    const tr = {
      top: rect.top - PADDING,
      left: rect.left - PADDING,
      width: rect.width + PADDING * 2,
      height: rect.height + PADDING * 2,
    };
    setTargetRect(tr);

    // Calculate tooltip position
    const tooltipW = 320;
    const tooltipH = 180;
    const gap = 12;

    let top: number;
    let left: number;
    let placement: string;

    // Sidebar items: place tooltip to the right of the element
    if (tr.left < 100 && tr.width < 100) {
      left = tr.left + tr.width + gap;
      top = tr.top;
      placement = "right";
      if (left + tooltipW > window.innerWidth - 20) {
        left = tr.left - tooltipW - gap;
        placement = "left";
      }
    } else {
      // Default: place below
      top = tr.top + tr.height + gap;
      left = tr.left;
      placement = "bottom";

      // If not enough space below, place above
      if (top + tooltipH > window.innerHeight - 20) {
        top = tr.top - tooltipH - gap;
        placement = "top";
      }
    }

    // Clamp horizontal within viewport
    if (left + tooltipW > window.innerWidth - 20) {
      left = window.innerWidth - tooltipW - 20;
    }
    if (left < 20) left = 20;

    // Clamp vertical within viewport
    if (top + tooltipH > window.innerHeight - 20) {
      top = window.innerHeight - tooltipH - 20;
    }
    if (top < 20) top = 20;

    setTooltipPos({ top, left, placement });
  }, [step]);

  useEffect(() => {
    measureTarget();
    const timer = setTimeout(measureTarget, 100);
    window.addEventListener("resize", measureTarget);
    window.addEventListener("scroll", measureTarget, true);
    return () => {
      clearTimeout(timer);
      window.removeEventListener("resize", measureTarget);
      window.removeEventListener("scroll", measureTarget, true);
    };
  }, [measureTarget, currentStep]);

  // Scroll target into view
  useEffect(() => {
    if (!step) return;
    const el = document.querySelector(step.targetSelector);
    if (el) {
      el.scrollIntoView({ behavior: "smooth", block: "center" });
      // Re-measure after scroll animation completes
      const scrollTimer = setTimeout(measureTarget, 600);
      return () => clearTimeout(scrollTimer);
    }
  }, [step, currentStep, measureTarget]);

  const handleNext = () => {
    if (currentStep < steps.length - 1) {
      setCurrentStep(currentStep + 1);
    } else {
      onComplete();
    }
  };

  const handleBack = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  if (!step) return null;

  // Build clip-path to create a "hole" around the target
  const clipPath = targetRect
    ? `polygon(
        0% 0%, 0% 100%, 
        ${targetRect.left}px 100%, 
        ${targetRect.left}px ${targetRect.top}px, 
        ${targetRect.left + targetRect.width}px ${targetRect.top}px, 
        ${targetRect.left + targetRect.width}px ${targetRect.top + targetRect.height}px, 
        ${targetRect.left}px ${targetRect.top + targetRect.height}px, 
        ${targetRect.left}px 100%, 
        100% 100%, 100% 0%
      )`
    : undefined;

  return (
    <div className="fixed inset-0 z-[9999]">
      {/* Dark overlay with hole */}
      <div
        className="absolute inset-0 bg-black/70 transition-all duration-300"
        style={{ clipPath }}
        onClick={onSkip}
      />

      {/* Highlight border around target */}
      {targetRect && (
        <div
          className="pointer-events-none absolute rounded-lg border-2 border-primary transition-all duration-300"
          style={{
            top: targetRect.top,
            left: targetRect.left,
            width: targetRect.width,
            height: targetRect.height,
          }}
        />
      )}

      {/* Tooltip */}

      <AnimatePresence mode="wait">
        <motion.div
          key={currentStep}
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -8 }}
          transition={{ duration: 0.2 }}
          className="absolute z-[10000] w-80 rounded-xl border border-border bg-card p-5 shadow-2xl"
          style={{ top: tooltipPos.top, left: tooltipPos.left }}
        >
          {/* Step indicator */}
          <div className="mb-3 flex items-center justify-between">
            <div className="flex items-center gap-1.5">
              {steps.map((_, i) => (
                <div
                  key={i}
                  className={`h-1.5 rounded-full transition-all ${
                    i === currentStep ? "w-6 bg-primary" : "w-1.5 bg-muted-foreground/30"
                  }`}
                />
              ))}
            </div>
            <button
              onClick={onSkip}
              className="p-1 text-muted-foreground transition-colors hover:text-foreground"
              aria-label="Skip guide"
            >
              <X className="h-4 w-4" />
            </button>
          </div>

          {/* Content */}
          <h3 className="mb-1 text-base font-semibold text-foreground">{step.title}</h3>
          <p className="mb-4 text-sm leading-relaxed text-muted-foreground">{step.description}</p>

          {/* Navigation */}
          <div className="flex items-center justify-between">
            <button
              onClick={onSkip}
              className="text-xs text-muted-foreground transition-colors hover:text-foreground"
            >
              Skip guide
            </button>
            <div className="flex items-center gap-2">
              {currentStep > 0 && (
                <Button variant="ghost" size="sm" onClick={handleBack} className="h-8 px-3">
                  <ChevronLeft className="mr-1 h-4 w-4" />
                  Back
                </Button>
              )}
              <Button size="sm" onClick={handleNext} className="h-8 px-4">
                {currentStep < steps.length - 1 ? (
                  <>
                    Next
                    <ChevronRight className="ml-1 h-4 w-4" />
                  </>
                ) : (
                  "Done"
                )}
              </Button>
            </div>
          </div>
        </motion.div>
      </AnimatePresence>
    </div>
  );
};

export default OnboardingGuide;
